import React, { createContext, useContext, useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";
import { NotificationManager } from "react-notifications";
import SkeletonLoading from "./components/utils/SkeletonLoading";
import { deploymentStorageKeys, getAuthToken } from "./services/deploymentConfig";
import { clearLocalSession, getStoredLocalSession } from "./services/localAuthService";

// Constants
const WARNING_BEFORE = 60 * 1000; // 1 minute warning
const TIMEOUT = 20 * 60 * 1000;   // 20 minutes idle
const UserContext = createContext(null);

const UserProviderComponent = ({ children, history }) => {
  const [user, setUser] = useState(null);
  const [hospitalId, setHospitalId] = useState("");
  const [hospitalDetails, setHospitalDetails] = useState(null);
  const [platformUser, setPlatformUser] = useState(null);
  const [isReady, setIsReady] = useState(false);

  const clearUserSession = ({ notify = true } = {}) => {
    setUser(null);
    setHospitalId("");
    setHospitalDetails(null);
    setPlatformUser(null);
    sessionStorage.clear();
    clearLocalSession();
    if (notify) NotificationManager.warning("You’ve been logged out due to inactivity.");
    history.push("/login");
  };

  const setUserWithExpiry = (userData) => {
    setUser(userData);
    sessionStorage.setItem("userData", JSON.stringify(userData));
  };

  const setHospitalIdWithStorage = (id) => {
    setHospitalId(id);
    sessionStorage.setItem("hospitalId", id);
  };

  const setHospitalDetailsWithStorage = (details) => {
    setHospitalDetails(details);
    sessionStorage.setItem("hospitalDetails", JSON.stringify(details));
  };

  const enterFacilitySession = (facility, operatorUser = user) => {
    if (!facility?.hospitalId || !operatorUser) return;

    clearLocalSession();
    sessionStorage.setItem(deploymentStorageKeys.cloudFacilitySession, "true");

    const backup = platformUser || operatorUser;
    setPlatformUser(backup);
    sessionStorage.setItem("platformUserData", JSON.stringify(backup));

    const facilityUser = {
      ...operatorUser,
      hospitalId: facility.hospitalId,
      hospitalName: facility.hospitalName || facility.name || "",
      roleName: operatorUser.roleName || "Admin",
      isSuperAdmin:
        operatorUser.isSuperAdmin ||
        operatorUser.isOwner ||
        operatorUser.canModifyFacilities ||
        operatorUser.canEnterFacilities ||
        operatorUser.platformAccess === "owner" ||
        operatorUser.platformAccess === "deployment" ||
        operatorUser.platformRole === "owner" ||
        operatorUser.platformRole === "deployment",
      isPlatformOperator: true,
      activeFacilityId: facility.hospitalId,
      activeFacilityName: facility.hospitalName || facility.name || "",
    };

    setUserWithExpiry(facilityUser);
    setHospitalIdWithStorage(facility.hospitalId);
    setHospitalDetailsWithStorage(facility);
  };

  const returnToOperationsConsole = () => {
    const backup = platformUser || JSON.parse(sessionStorage.getItem("platformUserData") || "null");
    if (!backup) return;
    setUserWithExpiry(backup);
    setHospitalId("");
    setHospitalDetails(null);
    sessionStorage.removeItem("hospitalId");
    sessionStorage.removeItem("hospitalDetails");
    sessionStorage.removeItem(deploymentStorageKeys.cloudFacilitySession);
  };

  useIdleTimer({
    timeout: TIMEOUT,
    promptBeforeIdle: WARNING_BEFORE,
    onIdle: () => {
      if (user) {
        clearUserSession();
      }
    },
    debounce: 500,
  });

  // Load session from storage on first mount
  useEffect(() => {
    const localSession = getStoredLocalSession();
    const hasLocalToken = !!getAuthToken();
    const storedUser = sessionStorage.getItem("userData");
    const storedHospitalId = sessionStorage.getItem("hospitalId");
    const storedHospitalDetails = sessionStorage.getItem("hospitalDetails");
    const storedPlatformUser = sessionStorage.getItem("platformUserData");

    if (storedUser) setUser(JSON.parse(storedUser));
    else if (hasLocalToken && localSession?.user) setUser(localSession.user);

    if (storedHospitalId) setHospitalId(storedHospitalId);
    else if (hasLocalToken && localSession?.hospitalId) setHospitalId(localSession.hospitalId);

    if (storedHospitalDetails) setHospitalDetails(JSON.parse(storedHospitalDetails));
    else if (hasLocalToken && localSession?.hospital) setHospitalDetails(localSession.hospital);

    if (storedPlatformUser) setPlatformUser(JSON.parse(storedPlatformUser));

    setIsReady(true); // allow app to render
  }, []);

  // Keep sessionStorage in sync on updates
  useEffect(() => {
    if (user) sessionStorage.setItem("userData", JSON.stringify(user));
    else sessionStorage.removeItem("userData");
  }, [user]);

  useEffect(() => {
    if (hospitalId) sessionStorage.setItem("hospitalId", hospitalId);
    else sessionStorage.removeItem("hospitalId");
  }, [hospitalId]);

  useEffect(() => {
    if (hospitalDetails) sessionStorage.setItem("hospitalDetails", JSON.stringify(hospitalDetails));
    else sessionStorage.removeItem("hospitalDetails");
  }, [hospitalDetails]);

  // Optional splash screen
  if (!isReady) return <SkeletonLoading/>

  return (
    <UserContext.Provider
      value={{
        user,
        setUser: setUserWithExpiry,
        hospitalId,
        setHospitalId: setHospitalIdWithStorage,
        hospitalDetails,
        setHospitalDetails: setHospitalDetailsWithStorage,
        platformUser,
        enterFacilitySession,
        returnToOperationsConsole,
        clearUserSession,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserProvider = withRouter(UserProviderComponent);
export const useUser = () => useContext(UserContext);
